import { useState, useRef, useEffect } from "react";
import { getActiveQuestions } from "containers/questions/api";
import { initialMetaForTable, DEBOUNCE_DELAY } from "constants/common";
import AsyncSelect from "components/common/select/AsyncSelect";
import Input from "components/common/input";
import debounce from "lodash/debounce";
import { ErrorMessage, Formik } from "formik";
import * as yup from "yup";

const useDebouncedFetchQuestions = () => {
  const debouncedFetch = useRef(
    debounce(async (inputValue, callback) => {
      try {
        const response = await getActiveQuestions({
          search: inputValue,
          status: "active",
        });
        let formattedOptions = response?.questions?.map((el) => ({
          value: el.id,
          label: el.description,
        }));

        callback(formattedOptions || []);
      } catch (error) {
        callback([]);
      }
    }, DEBOUNCE_DELAY)
  ).current;

  return debouncedFetch;
};

const initialValues = {
  name: "",
  question_id: "",
  question_label: "",
};

const templateValidationSchema = yup.object({
  name: yup.string().required("Required"),
  question_id: yup.string().required("Required"),
});

const TemplateForm = ({
  handleTemplateSubmittion,
  handleClose,
  currentTemplateEdit,
}) => {
  const [questionList, setQuestionList] = useState([]);
  const debouncedFetchQuestionsData = useDebouncedFetchQuestions();

  useEffect(() => {
    (async () => {
      const result = await getActiveQuestions({
        ...initialMetaForTable,
        page: 1,
      });
      if (result?.questions) setQuestionList(result?.questions);
    })();
  }, [debouncedFetchQuestionsData]);

  const formInitialValues = currentTemplateEdit
    ? {
        name: currentTemplateEdit.name,
        question_id: currentTemplateEdit.question_id,
        question_label: currentTemplateEdit.question?.description || "",
      }
    : initialValues;

  return (
    <Formik
      initialValues={formInitialValues}
      validationSchema={templateValidationSchema}
      onSubmit={(values, { setSubmitting, setErrors }) => {
        handleTemplateSubmittion(values, setSubmitting, setErrors);
      }}
    >
      {({
        handleChange,
        values,
        handleSubmit,
        setFieldValue,
        isSubmitting,
      }) => {
        const selectedQuestion =
          values.question_id && values.question_label
            ? { value: values.question_id, label: values.question_label }
            : null;

        return (
          <div className="question-model">
            <hr className="custom-model-line mt-0" />
            <form className="form-main" onSubmit={handleSubmit}>
              <div className="input-flex row">
                <div className="col-sm-12">
                  <Input
                    handleChange={handleChange}
                    placeholder="e.g. Bay Area Housing Support"
                    type="text"
                    label="Name"
                    isRequired
                    value={values.name}
                    name="name"
                    error={
                      <ErrorMessage
                        className="error-message"
                        component="p"
                        name="name"
                      />
                    }
                  />
                </div>
                <div className="col-sm-12">
                  <AsyncSelect
                    label="Questions"
                    isRequired
                    options={questionList.map((el) => ({
                      ...el,
                      value: el.id,
                      label: el.description,
                    }))}
                    dataCallback={(inputValue) =>
                      new Promise((resolve) => {
                        debouncedFetchQuestionsData(inputValue, (options) => {
                          if (!options || options.length === 0) {
                            resolve([]);
                          } else {
                            resolve(options);
                          }
                        });
                      })
                    }
                    selectedValue={selectedQuestion}
                    placeholder="Select Question"
                    handleChange={(selectedOption) => {
                      setFieldValue("question_id", selectedOption.value);
                      setFieldValue("question_label", selectedOption.label);
                    }}
                    error={
                      <ErrorMessage
                        className="error-message"
                        component="p"
                        name="question_id"
                      />
                    }
                  />
                </div>
              </div>

              <hr className="custom-modal-line mt-4" />

              <div className="action gap-2">
                <button
                  type="button"
                  className="secondary-btn w-50"
                  onClick={handleClose}
                >
                  Cancel
                </button>

                {isSubmitting ? (
                  <button className="primary-btn w-50" disabled>
                    <span
                      className="spinner-grow spinner-grow-sm"
                      role="status"
                      aria-hidden="true"
                    />
                    Submitting...
                  </button>
                ) : (
                  <button className="primary-btn w-50" type="submit">
                    {currentTemplateEdit ? "Update Template" : "Add Template"}
                  </button>
                )}
              </div>
            </form>
          </div>
        );
      }}
    </Formik>
  );
};

export default TemplateForm;
